import { Grid, Button, TextField, Alert, Divider } from '@mui/material';
import { Box } from '@mui/system';
import SendIcon from '@mui/icons-material/Send';
import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { RegexHelper } from 'src/shared/helpers/regexHelper';
import { previewGroupMessage, updatePreviewGroupMessage } from 'src/shared/services/groupMessageService';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { toast } from 'react-toastify';
import { GroupMessagePreviewPost } from '../models/groupMessagePreviewPost';
import { GroupMessagePreviewPut } from '../models/groupMessagePreviewPut';

type GroupMessageEmailPreviewModalProps = {
  modalOpen: boolean;
  // newId skickas med vid POST /Preview (nyskapat utskick) så anroparen kan navigera till redigerasidan.
  onSave: (newId?: number) => void;
  onClose: () => void;
  // Befintligt utskick — om angivet används PUT /Preview som återanvänder existerande reg_circular istället för att skapa ny.
  existingId?: number;
};

export const GroupMessageEmailPreviewModal = (props: GroupMessageEmailPreviewModalProps) => {
  const { modalOpen, onSave, onClose, existingId } = props;
  const isMounted = useIsMounted();
  const { t } = useTranslation(['common', 'groupMessage']);

  const [loading, setLoading] = useState<boolean>(false);
  const [recipients, setRecipients] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const { getValues } = useFormContext();

  const onHandleSave = useCallback(
    (newId?: number) => {
      onSave(newId);
    },
    [onSave],
  );

  const onHandleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSendPreview = useCallback(async () => {
    setLoading(true);
    try {
      if (existingId) {
        // Befintligt utskick — PUT /Preview återanvänder existerande reg_circular utan att skapa ny.
        const dataToPut: GroupMessagePreviewPut = {
          id: existingId,
          title: getValues('subject'),
          message: getValues('body'),
          receivers: recipients,
          files: getValues('files'),
          sender: getValues('sender'),
          senderTitle: getValues('senderTitle'),
          documentIds: getValues('documentIds'),
        };
        await updatePreviewGroupMessage(dataToPut);
        if (isMounted()) {
          onHandleSave();
          toast.success(<>{t('groupMessage:preview.sendSucess')}</>);
        }
      } else {
        const dataToPost: GroupMessagePreviewPost = {
          title: getValues('subject'),
          message: getValues('body'),
          receivers: recipients,
          files: getValues('files'),
          sender: getValues('sender'),
          senderTitle: getValues('senderTitle'),
        } as GroupMessagePreviewPost;
        const res = await previewGroupMessage(dataToPost);
        if (isMounted()) {
          // Backend returnerar circular_id
          const newId = res.data;
          onHandleSave(newId);
          toast.success(<>{t('groupMessage:preview.sendSucess')}</>);
        }
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      setLoading(false);
    }
  }, [existingId, getValues, isMounted, onHandleSave, recipients, t]);

  const handleChangeRecipient = (val: string) => {
    if (!val.match(RegexHelper.emailAddresses)) {
      setErrorMessage(t('common:validationMessages.multipleEmail'));
    } else {
      setErrorMessage('');
    }
    setRecipients(val);
  };

  return (
    <GenericDialog
      open={modalOpen}
      padding={0}
      maxWidth='sm'
      dialogTitle={t('groupMessage:preview.modalHeader')}
      onClose={onHandleClose}
      onSave={onHandleSave}
      dialogContent={
        <FormLoaderWrapper loading={loading}>
          <>
            <Alert severity='info'>{t('groupMessage:preview.alertDescription')}</Alert>
            <Divider />
            <Box sx={{ p: 2 }}>
              <GridWrapper>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size='small'
                    label={t('groupMessage:preview.emailLabel')}
                    value={recipients}
                    placeholder={t('groupMessage:preview.emailPlaceholder')}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      handleChangeRecipient(event.target.value);
                    }}
                    error={!!errorMessage}
                    helperText={errorMessage || t('groupMessage:formFields.recipients_helperText')}
                  />
                </Grid>
              </GridWrapper>
            </Box>
          </>
        </FormLoaderWrapper>
      }
      dialogActions={
        <>
          <Button variant='outlined' disableElevation onClick={onHandleClose}>
            {t('common:button.cancel')}
          </Button>
          <Button
            variant='contained'
            disableElevation
            disabled={!!errorMessage || !recipients}
            onClick={handleSendPreview}
            endIcon={<SendIcon aria-hidden />}
          >
            {t('common:button.saveAndSend')}
          </Button>
        </>
      }
    />
  );
};
